"use client";

import { cn } from "@/lib/utils";
import React, { CSSProperties, ElementType, MouseEventHandler } from "react";

interface TypographyProps {
  as?: ElementType;
  variant?:
  | "display-2xl"
  | "display-xl"
  | "display-lg"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "subtitle1"
  | "subtitle2"
  | "body1"
  | "body2"
  | "body-xs"
  | "paragraph-lg"
  | "paragraph"
  | "overline"
  | "button-md"
  | "button-sm"
  | "button-xs";
  mdVariant?: TypographyProps["variant"];
  lgVariant?: TypographyProps["variant"];
  className?: string;
  children: React.ReactNode;
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLElement>;
}

type TypographyVariant = NonNullable<TypographyProps["variant"]>;
const variantStyles: Record<TypographyVariant, string> = {
  "display-2xl": "text-[72px] font-semibold leading-[76px] tracking-[0px]",
  "display-xl": "text-[60px] font-semibold leading-[68px] tracking-[0px]",
  "display-lg": "text-[48px] font-semibold leading-[56px] tracking-[0px]",
  h1: "text-[40px] font-semibold leading-[48px] tracking-[0px]",
  h2: "text-[32px] font-semibold leading-[40px] tracking-[0px]",
  h3: "text-[28px] font-semibold leading-[36px] tracking-[0px]",
  h4: "text-[24px] font-semibold leading-[32px] tracking-[0px]",
  h5: "text-[20px] font-semibold leading-[28px] tracking-[0px]",
  h6: "text-[18px] font-semibold leading-[24px] tracking-[0px]",
  subtitle1: "text-[16px] font-semibold leading-[24px] tracking-[0px]",
  subtitle2: "text-[14px] font-semibold leading-[20px] tracking-[0px]",
  body1: "text-[16px] font-normal leading-[22px] tracking-[0px]",
  body2: "text-[14px] font-normal leading-[20px] tracking-[0px]",
  "body-xs": "text-[12px] font-normal leading-[16px] tracking-[0px]",
  "paragraph-lg": "text-[16px] font-normal leading-[26px] tracking-[0px]",
  paragraph: "text-[14px] font-normal leading-[22px] tracking-[0px]",
  overline: "text-[10px] font-semibold leading-[16px] tracking-[0px]",
  "button-md": "text-[16px] font-semibold leading-[24px] tracking-[0px]",
  "button-sm": "text-[14px] font-semibold leading-[20px] tracking-[0px]",
  "button-xs": "text-[12px] font-semibold leading-[16px] tracking-[0px]",
};

const mdVariantStyles: Record<TypographyVariant, string> = {
  "display-2xl": "md:text-[72px] md:font-semibold md:leading-[76px] md:tracking-[0px]",
  "display-xl": "md:text-[60px] md:font-semibold md:leading-[68px] md:tracking-[0px]",
  "display-lg": "md:text-[48px] md:font-semibold md:leading-[56px] md:tracking-[0px]",
  h1: "md:text-[40px] md:font-semibold md:leading-[48px] md:tracking-[0px]",
  h2: "md:text-[32px] md:font-semibold md:leading-[40px] md:tracking-[0px]",
  h3: "md:text-[28px] md:font-semibold md:leading-[36px] md:tracking-[0px]",
  h4: "md:text-[24px] md:font-semibold md:leading-[32px] md:tracking-[0px]",
  h5: "md:text-[20px] md:font-semibold md:leading-[28px] md:tracking-[0px]",
  h6: "md:text-[18px] md:font-semibold md:leading-[24px] md:tracking-[0px]",
  subtitle1: "md:text-[16px] md:font-semibold md:leading-[24px] md:tracking-[0px]",
  subtitle2: "md:text-[14px] md:font-semibold md:leading-[20px] md:tracking-[0px]",
  body1: "md:text-[16px] md:font-normal md:leading-[22px] md:tracking-[0px]",
  body2: "md:text-[14px] md:font-normal md:leading-[20px] md:tracking-[0px]",
  "body-xs": "md:text-[12px] md:font-normal md:leading-[16px] md:tracking-[0px]",
  "paragraph-lg": "md:text-[16px] md:font-normal md:leading-[26px] md:tracking-[0px]",
  paragraph: "md:text-[14px] md:font-normal md:leading-[22px] md:tracking-[0px]",
  overline: "md:text-[10px] md:font-semibold md:leading-[16px] md:tracking-[0px]",
  "button-md": "md:text-[16px] md:font-semibold md:leading-[24px] md:tracking-[0px]",
  "button-sm": "md:text-[14px] md:font-semibold md:leading-[20px] md:tracking-[0px]",
  "button-xs": "md:text-[12px] md:font-semibold md:leading-[16px] md:tracking-[0px]",
};

export const Typography = ({
  as,
  variant = "body1",
  mdVariant,
  className,
  children,
  style,
  onClick,
}: TypographyProps) => {
  const Tag = as || "div";

  return (
    <Tag
      className={cn(
        variantStyles[variant],
        mdVariant && mdVariantStyles[mdVariant],
        "text-heading",
        className,
      )}
      style={style}
      onClick={onClick}
    >
      {children}
    </Tag>
  );
};